import classNames from "classnames";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { common_utils } from "../../../../../../../../../common/common_utils";
import {
	createRRuleSet,
	timeToRRuleTime,
} from "../../../../../../../../../common/repeat_utils";
import { EventDisplayData } from "../../../../../../../../../common/sesh_types";
import { RouterInferenceHelpers } from "../../../../../../../../../src/api/trpc/rootRouter";
import { NativeEventDisplayData } from "../../../../../../../../../src/shard_api/routes/getNativeEvents";
import { RemovingThrobber } from "../../../../../removing_throbber";
import { LoadingThrobber } from "../../../../base_components/loading_throbber";
import { ModifySettingsButton } from "../../../../base_components/modify_settings_button";
import { SeshPrimaryButton } from "../../../../base_components/sesh_primary_button";
import { Disabler } from "../../../../disabler";
import { GuildIcon } from "../../../../guild_icon";
import { useEventListQuery } from "../../../../hooks/use_event_list_query";
import { usePublicCalendarQuery } from "../../../../hooks/use_public_calendar_query";
import { useServerId } from "../../../../hooks/use_server_id";
import { useCalendarSearchParams } from "../../hooks/use_calendar_search_params";
import {
	PublicCalendarContext,
	usePublicCalendarContext,
} from "../hooks/use_public_calendar_context";
import { CalendarCell } from "./calendar_cell";
import { MobileCalendarCell } from "./mobile_calendar_cell";
import { MobileCalendarEventList } from "./mobile_calendar_event_list";

export const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function eventSorter(a: EventDisplayData, b: EventDisplayData) {
	const a_start_time = new Date(a.start_time_str).getTime();
	const b_start_time = new Date(b.start_time_str).getTime();

	if (a_start_time === b_start_time) {
		return (
			new Date(a.created_at_str).getTime() -
			new Date(b.created_at_str).getTime()
		);
	}

	return a_start_time - b_start_time;
}

function getRepeatedEventsThisMonth(
	repeat_items: RouterInferenceHelpers["event"]["list"]["output"]["repeat_items"],
	start_of_month: DateTime,
	start_of_month_index: number
) {
	if (!repeat_items) {
		return [];
	}

	const local_timezone = DateTime.local().zoneName;

	return repeat_items.flatMap((val) => {
		const rrule_set = createRRuleSet({
			rrule_str: val.rrule,
			start_time_js: new Date(
				val.repeat_instance_original_start_time_str
			),
			repeat_tz: val.repeat_tz,
			exdates: val.exdates,
			rdates: val.rdates,
		});

		const dates = rrule_set.between(
			timeToRRuleTime(new Date(val.start_time_str), val.repeat_tz),
			start_of_month
				.plus({ days: 7 * 6 - start_of_month_index })
				.toUTC(undefined, { keepLocalTime: true })
				.toJSDate(),
			false
		);

		return dates
			.map((date) => {
				const duration =
					new Date(val.end_time_str).getTime() -
					new Date(val.start_time_str).getTime();

				const start_time = DateTime.fromJSDate(date)
					.toUTC()
					.setZone(local_timezone, { keepLocalTime: true })
					.toUTC();

				// Ensure start time is excluded. Duplicates will occur if user viewing the event is in a different timezone than the event itself
				// Also exclude the original rrule time if the event was moved — that instance is already shown as a real event entity
				if (
					start_time.toMillis() ===
						new Date(val.start_time_str).getTime() ||
					start_time.toMillis() ===
						new Date(
							val.repeat_instance_original_start_time_str
						).getTime()
				) {
					return undefined;
				}

				return {
					...val,
					start_time_str: start_time.toISO(),
					end_time_str: start_time
						.plus({ milliseconds: duration })
						.toISO(),
					is_repeat_occurrence: true,
					initial_event_start_time_str: val.start_time_str,
				};
			})
			.filter((val) => val) as EventDisplayData[];
	});
}

export function DashboardCalendar(props: { viewToggler: JSX.Element }) {
	const server_id = useServerId();

	const initial_props = useInitialCalendarProps();

	const event_query = useEventListQuery({
		guild_id: server_id,
		sort: {
			type: "Date",
			direction: "ASC",
		},
		range: {
			begin: initial_props.start_of_month
				.minus({ days: initial_props.start_of_month_index })
				.toISO(),
			end: initial_props.start_of_month
				.plus({ days: 7 * 6 - initial_props.start_of_month_index })
				.toISO(),
		},
		search: "",
		include_completed: true,
	});

	return (
		<CalendarPage
			{...initial_props}
			headerElement={props.viewToggler}
			events_query={event_query}
		/>
	);
}

// Copied from web_ui because of seeming circular dependency issues
function setDocTitle(info: string, guild_name?: string) {
	const strings = [info];
	if (guild_name) {
		strings.push(guild_name);
	}
	strings.push("sesh.fyi");

	document.title = strings.join(" - ");
}

export function PublicCalendar() {
	const server_id = useServerId();

	const initial_props = useInitialCalendarProps();

	const events_query = usePublicCalendarQuery({
		guild_id: server_id,
		range: {
			begin: initial_props.start_of_month
				.minus({ days: initial_props.start_of_month_index })
				.toISO(),
			end: initial_props.start_of_month
				.plus({ days: 7 * 6 - initial_props.start_of_month_index })
				.toISO(),
		},
	});

	useEffect(() => {
		if (events_query.data?.guild_descriptor.name) {
			setDocTitle(
				"Calendar - " + events_query.data?.guild_descriptor.name
			);
		}
	}, [events_query.data?.guild_descriptor.name]);

	return (
		<PublicCalendarContext.Provider
			value={{
				is_public: true,
				authors: events_query.data?.authors ?? [],
				channels: events_query.data?.channels ?? [],
			}}
		>
			<CalendarPage
				{...initial_props}
				events_query={events_query}
				headerElement={
					<div className="flex items-center gap-2 text-xl font-bold">
						{events_query.data?.guild_descriptor.icon && (
							<GuildIcon
								server_id={server_id}
								hash={events_query.data?.guild_descriptor.icon}
								width={32}
							/>
						)}
						{events_query.data?.guild_descriptor.name}
					</div>
				}
			/>
		</PublicCalendarContext.Provider>
	);
}

function getLastSunday(date: DateTime) {
	return date.minus({ days: date.weekday % 7 });
}

function getNextSaturday(date: DateTime) {
	const desired_weekday = 6;
	const current_weekday = date.weekday;

	const day_difference = (desired_weekday - current_weekday + 7) % 7;
	return date.plus({ days: day_difference }).startOf("day");
}

type CalendarPagePropsType = {
	headerElement: JSX.Element;
	events_query:
		| ReturnType<typeof useEventListQuery>
		| ReturnType<typeof usePublicCalendarQuery>;
	current_month: DateTime;
	start_of_month: DateTime;
	start_of_month_index: number;
	month_offset: number;
};

export type CalendarEventDisplayData =
	| EventDisplayData
	| NativeEventDisplayData;

export function CalendarPage(props: CalendarPagePropsType) {
	const {
		events_query,
		month_offset,
		current_month,
		start_of_month,
		start_of_month_index,
	} = props;
	const search_params = useCalendarSearchParams();
	const history = useHistory();
	const server_id = useServerId();

	const { is_public } = usePublicCalendarContext();

	const repeated_events_this_month = useMemo(
		() =>
			getRepeatedEventsThisMonth(
				events_query.data?.repeat_items,
				start_of_month,
				start_of_month_index
			),
		[events_query.data?.repeat_items, start_of_month, start_of_month_index]
	);

	const [mobile_status, setIsMobile] = useState(
		window.innerWidth < 640
			? { selected_index: null as number | null }
			: null
	);

	function setMonthOffset(month_offset: number) {
		if (month_offset === 0) {
			search_params.delete("date");
			history.replace(search_params.toURL());
			return;
		}

		const new_date = DateTime.now()
			.startOf("month")
			.plus({ month: month_offset });

		search_params.set("date", `${new_date.year}-${new_date.month}`);

		history.replace(search_params.toURL());
	}

	useEffect(() => {
		function handleResize() {
			setIsMobile((prev) => {
				const is_mobile = !!prev;

				const changed_to_mobile = !is_mobile && window.innerWidth < 640;
				const changed_to_desktop =
					is_mobile && window.innerWidth >= 640;

				if (changed_to_mobile) {
					return {
						selected_index: null,
					};
				} else if (changed_to_desktop) {
					return null;
				}

				return prev;
			});
		}

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	if (!events_query.isSuccess) {
		return <LoadingThrobber />;
	}

	// Create a map of event day to an array of all events on that day
	const events_by_day = new Map<number, Array<CalendarEventDisplayData>>();

	const events = [
		...events_query.data.items,
		...repeated_events_this_month,
		...events_query.data.native_events,
	];

	for (const event of events) {
		const event_date = DateTime.fromJSDate(
			new Date(event.start_time_str)
		).startOf("day");

		// Round for DST fuckery
		const day = Math.round(event_date.diff(start_of_month).as("days"));
		if (!events_by_day.has(day)) {
			events_by_day.set(day, []);
		}
		events_by_day.get(day).push(event);
	}

	const start_of_cal = getLastSunday(start_of_month);
	const end_of_cal = getNextSaturday(start_of_month.endOf("month"));

	const rows_required =
		(Math.round(end_of_cal.diff(start_of_cal, "days").days) + 1) / 7;

	let elements: JSX.Element[] = [];
	for (let i = 0; i < 7 * rows_required; i++) {
		const day_offset = i - start_of_month_index;

		elements.push(
			mobile_status ? (
				<MobileCalendarCell
					key={`${day_offset}_mobile`}
					index={i}
					events={
						events_by_day.get(day_offset)?.sort(eventSorter) ?? []
					}
					day_offset={day_offset}
					start_of_month={start_of_month}
					is_selected={mobile_status.selected_index === day_offset}
					setIsSelected={(selected_index) =>
						setIsMobile({
							selected_index,
						})
					}
				/>
			) : (
				<CalendarCell
					key={`${day_offset}_tablet`}
					index={i}
					events={
						events_by_day.get(day_offset)?.sort(eventSorter) ?? []
					}
					day_offset={day_offset}
					start_of_month={start_of_month}
				/>
			)
		);
	}

	const is_loading = events_query.isPreviousData;

	return (
		<Disabler disabled={is_loading}>
			<div className="flex flex-col gap-2">
				<div className="flex flex-col sm:flex-row">
					{props.headerElement}
					<div className="ml-auto flex items-center gap-2 text-xl font-bold">
						{is_loading && <RemovingThrobber />}
						<div className="flex flex-shrink-0">
							<button
								className="px-4 font-bold text-sesh-primary hover:text-sesh-dark"
								onClick={() => {
									if (is_loading) return;

									setMonthOffset(month_offset - 1);
								}}
								disabled={is_loading}
							>
								<i className="fas fa-chevron-left"></i>
							</button>
							<div className="font-mono">
								{current_month.monthShort} {current_month.year}
							</div>
							<button
								className="px-4 font-bold text-sesh-primary hover:text-sesh-dark"
								onClick={() => {
									if (is_loading) return;
									setMonthOffset(month_offset + 1);
								}}
								disabled={is_loading}
							>
								<i className="fas fa-chevron-right"></i>
							</button>
						</div>
						<SeshPrimaryButton
							custom_size="small"
							onClick={() => {
								if (is_loading) return;
								setMonthOffset(0);
							}}
						>
							Today
						</SeshPrimaryButton>
						<div className="ml-4">
							{is_public ? (
								<a
									className="manual-link"
									href={common_utils.getEventListURL(
										server_id
									)}
								>
									Dashboard
								</a>
							) : (
								<ModifySettingsButton
									hide_arrow
									onClick={() =>
										// TODO: should make this back link business more reusable
										history.push(
											common_utils.getGuildCalendarSettingsURL(
												server_id
											),
											{
												back_link:
													history.location.pathname,
											}
										)
									}
								>
									<div className="flex items-center gap-2">
										<i className="fas fa-cog"></i>Settings
									</div>
								</ModifySettingsButton>
							)}
						</div>
					</div>
				</div>
				<div
					className={classNames("grid w-full grid-cols-7", {
						"grid-rows-4 sm:h-[34.4rem]": rows_required === 4,
						"grid-rows-5 sm:h-[43rem]": rows_required === 5,
						"grid-rows-6 sm:h-[51.6rem]": rows_required === 6,
					})}
				>
					{elements}
				</div>
				{mobile_status?.selected_index != null && (
					<MobileCalendarEventList
						date={start_of_month
							.plus({ days: mobile_status.selected_index })
							.startOf("day")}
						events={
							events_by_day
								.get(mobile_status?.selected_index)
								?.sort(eventSorter) ?? []
						}
					/>
				)}
			</div>
		</Disabler>
	);
}

function useInitialCalendarProps(): Omit<
	CalendarPagePropsType,
	"events_query" | "headerElement"
> {
	const search_params = useCalendarSearchParams();

	const date_from_url = search_params.get("date");

	let desired_date: DateTime | undefined = undefined;
	if (date_from_url) {
		const [year, month] = date_from_url
			.split("-")
			.map((val) => parseInt(val));
		desired_date = DateTime.fromObject({
			year,
			month,
		});
	} else {
		desired_date = DateTime.now().startOf("month");
	}

	const now_start_of_month = DateTime.local().startOf("month");

	// Use luxon to get the difference in months between now and desired date

	const month_offset = Math.round(
		desired_date.diff(now_start_of_month, "months").as("months")
	);

	const current_month = DateTime.now().plus({ month: month_offset });
	const start_of_month = current_month.startOf("month");
	const start_of_month_index = start_of_month.weekday % 7;

	return {
		current_month,
		start_of_month,
		start_of_month_index,
		month_offset,
	};
}
